import { useCallback, useRef, useState } from "react"
import { httpSharePointRepository } from "../../data/repositories/sharepoint-repository"

export const isConnectedMessage = (
  event: MessageEvent,
  allowedOrigin: string,
): string | null => {
  if (event.origin !== allowedOrigin) return null
  const data = event.data as { type?: string; connection_id?: string } | null
  if (!data || data.type !== "sharepoint-connected") return null
  if (typeof data.connection_id !== "string" || !data.connection_id) return null
  return data.connection_id
}

// Cache the opaque connection handle per KB in sessionStorage so reopening the
// dialog within the same tab reuses the still-valid backend session (Redis TTL
// 1h) instead of forcing a fresh Microsoft login every time. sessionStorage
// (not localStorage) keeps the handle scoped to the tab and gone on tab close.
const storageKey = (kbId: string) => `sharepoint:conn:${kbId}`

const readStoredConnection = (kbId: string): string | null => {
  try {
    return window.sessionStorage.getItem(storageKey(kbId))
  } catch {
    return null
  }
}

export const useSharePointAuth = (kbId: string) => {
  const [connectionId, setConnectionId] = useState<string | null>(() =>
    readStoredConnection(kbId),
  )
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const popupRef = useRef<Window | null>(null)

  const persist = useCallback(
    (id: string) => {
      setConnectionId(id)
      try {
        window.sessionStorage.setItem(storageKey(kbId), id)
      } catch {
        /* sessionStorage unavailable — connection still works in-memory */
      }
    },
    [kbId],
  )

  // Drop a dead/expired connection so the UI falls back to the connect screen.
  const clearConnection = useCallback(() => {
    setConnectionId(null)
    setError(null)
    try {
      window.sessionStorage.removeItem(storageKey(kbId))
    } catch {
      /* ignore */
    }
  }, [kbId])

  const connect = useCallback(async () => {
    setError(null)
    setIsConnecting(true)
    try {
      const url = await httpSharePointRepository.startAuth(kbId)
      // The Microsoft redirect lands on our own /oauth-callback route (same
      // origin), which exchanges the code and postMessages the connection id.
      const allowed = window.location.origin

      await new Promise<void>((resolve, reject) => {
        const onMessage = (event: MessageEvent) => {
          const id = isConnectedMessage(event, allowed)
          if (!id) return
          window.removeEventListener("message", onMessage)
          persist(id)
          resolve()
        }
        window.addEventListener("message", onMessage)
        popupRef.current = window.open(url, "sharepoint-auth", "width=520,height=680")
        if (!popupRef.current) {
          window.removeEventListener("message", onMessage)
          reject(new Error("Popup bị chặn. Vui lòng cho phép popup và thử lại."))
        }
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : "Xác thực thất bại")
      throw e
    } finally {
      setIsConnecting(false)
    }
  }, [kbId, persist])

  return { connect, connectionId, isConnecting, error, clearConnection }
}
